/**
 * RIFT — personnel.
 *
 * The full operator roster, opened from the home screen's PERSONNEL nav item.
 * Self-contained like `armory.js`: one class, its own scoped `.rp-` stylesheet,
 * no assets beyond the shared hand-authored SVG in `art.js`. Reads the same
 * roster as the home screen's inline OPERATOR FILE panel (`operators.js`), so
 * picking an operator here and picking one there can never disagree.
 *
 *     const personnel = new Personnel(host, { activeId, playerLevel, onSelect, onClose });
 *     personnel.show();
 *
 * `onSelect(id)` fires when the player sets a new active operator — the home
 * screen owns which index that is, this screen only ever reports a choice.
 * Setting an active operator is a pure front-end preference (unlike the
 * armory's purchases, nothing here is server-authoritative), so it is applied
 * immediately rather than staged behind a save button.
 */
import { OPERATORS } from './operators.js';
import { OPERATOR_SVG } from './art.js';
import { HUD_TOKENS, HUD_CSS, mountCorners } from './theme.js';

const STYLE_ID = 'rp-personnel-style';

export class Personnel {
  constructor(host, opts = {}) {
    this.host = host || document.body;
    this.onSelect = opts.onSelect ?? null;
    this.onClose = opts.onClose ?? null;
    this.playerLevel = opts.playerLevel ?? 1;
    this.activeId = opts.activeId ?? OPERATORS[0]?.id ?? null;
    this.visible = false;
    this._cards = new Map();

    this._injectStyle();
    this._build();
  }

  _el(tag, cls, parent, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  _build() {
    this.root = this._el('section', 'rp-root', null);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Personnel');
    this.root.hidden = true;
    mountCorners(this.root);

    const head = this._el('div', 'rp-head', this.root);
    const headL = this._el('div', null, head);
    this._el('div', 'hud-eyebrow', headL, 'PERSONNEL');
    this._el('h1', 'rp-title hud-title', headL, 'OPERATOR ROSTER');
    this._el(
      'p',
      'rp-copy',
      headL,
      'Every operator on file. Set one active and their kit follows you into the next deployment.'
    );
    const close = this._el('button', 'hud-close rp-headclose', head, 'CLOSE');
    close.type = 'button';
    close.addEventListener('click', () => this._close());

    this.grid = this._el('div', 'rp-grid', this.root);
    for (const op of OPERATORS) this._card(op);

    const foot = this._el('div', 'rp-foot', this.root);
    this.flash = this._el('div', 'rp-flash', foot, '');
    const closeBtn = this._el('button', 'hud-btn rp-close', foot, 'CLOSE');
    closeBtn.type = 'button';
    closeBtn.addEventListener('click', () => this._close());

    this.host.appendChild(this.root);
  }

  _card(op) {
    const locked = (op.unlockLevel ?? 1) > this.playerLevel;
    const c = this._el('div', 'rp-card' + (locked ? ' rp-locked' : ''), this.grid);

    const portrait = this._el('div', 'rp-portrait', c);
    portrait.innerHTML = OPERATOR_SVG;
    if (locked) {
      const lock = this._el('div', 'rp-lockover', portrait);
      this._el('div', 'rp-lockicon', lock, '⌁');
      this._el('div', 'rp-locktext', lock, `LOCKED — LVL ${op.unlockLevel}`);
    }

    const body = this._el('div', 'rp-body', c);
    const top = this._el('div', 'rp-top', body);
    this._el('span', 'rp-name', top, op.name);
    this._el('span', 'rp-kit', top, op.kit);
    this._el('p', 'rp-tagline', body, op.tagline);

    const stats = this._el('div', 'rp-stats', body);
    for (const [k, v] of [
      ['PRIMARY', op.weapon],
      ['SIDEARM', op.sidearm],
      ['UTILITY', op.utility],
    ]) {
      const row = this._el('div', 'rp-stat-row', stats);
      this._el('span', 'rp-stat-k', row, k);
      this._el('span', 'rp-stat-v', row, v);
    }

    const record = this._el('div', 'rp-record', body);
    for (const [v, k] of [
      [op.missions, 'MISSIONS'],
      [op.kd.toFixed(2), 'K/D'],
      [op.winRate + '%', 'WIN'],
      [op.streak, 'STREAK'],
    ]) {
      const cell = this._el('div', 'rp-record-cell', record);
      this._el('span', 'rp-record-v', cell, String(v));
      this._el('span', 'rp-record-k', cell, k);
    }

    const action = this._el('button', 'hud-btn rp-action', body, locked ? 'LOCKED' : 'SET ACTIVE');
    action.type = 'button';
    action.disabled = locked;
    action.addEventListener('click', () => this._select(op.id));

    this._cards.set(op.id, { el: c, action });
    this._paintActive();
  }

  _select(id) {
    this.activeId = id;
    this._paintActive();
    try {
      this.onSelect?.(id);
    } catch (err) {
      console.warn('[personnel] onSelect threw', err);
    }
    this._say(`${OPERATORS.find((o) => o.id === id)?.name ?? id} SET ACTIVE`);
  }

  _paintActive() {
    for (const [id, row] of this._cards) {
      const on = id === this.activeId;
      row.el.classList.toggle('rp-active', on);
      if (!row.action.disabled) row.action.textContent = on ? 'ACTIVE' : 'SET ACTIVE';
    }
  }

  _say(msg) {
    this.flash.textContent = msg;
    this.flash.classList.remove('rp-flash-on');
    void this.flash.offsetWidth;
    this.flash.classList.add('rp-flash-on');
  }

  _close() {
    this.hide();
    this.onClose?.();
  }

  show() {
    if (this.visible) return;
    this.visible = true;
    this.root.hidden = false;
    void this.root.offsetWidth;
    this.root.classList.add('rp-live');
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove('rp-live');
    this.root.hidden = true;
  }

  dispose() {
    this.hide();
    try {
      this.root?.remove();
    } catch {
      /* already gone */
    }
  }
}

const CSS = `
/* The display:flex below outranks the UA's [hidden]{display:none}, so without
   this closing the screen leaves a transparent z-index:60 sheet across the
   viewport that silently eats every click on the menu underneath. */
.rp-root[hidden]{display:none!important}
.rp-root {
  ${HUD_TOKENS}
  position:fixed; inset:0; z-index:60; display:flex; flex-direction:column;
  gap:clamp(10px,1.6vh,20px); padding:clamp(16px,3vh,44px) clamp(18px,4vw,64px);
  background:
    radial-gradient(60% 55% at 82% 18%, rgba(182,146,255,.16) 0%, transparent 60%),
    radial-gradient(55% 60% at 14% 88%, rgba(255,176,42,.18) 0%, transparent 65%),
    radial-gradient(140% 100% at 50% 50%, rgba(22,32,42,.92) 0%, rgba(4,6,9,.99) 70%),
    linear-gradient(180deg, #0a0e14 0%, #04060a 100%);
  color:var(--ink); font-family:var(--ff); text-transform:uppercase; letter-spacing:.05em;
  opacity:0; transition:opacity .22s ease; overflow:hidden;
}
.rp-root.rp-live { opacity:1; }
.rp-root > * { position:relative; z-index:1; }
${HUD_CSS}

.rp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:28px; flex:0 0 auto;
  padding:24px 30px;
  background: linear-gradient(90deg, rgba(18,26,34,.96) 0%, rgba(10,16,21,.85) 60%, rgba(8,13,17,.55) 100%);
  border:1px solid rgba(190,214,230,.3); clip-path: var(--shear);
  position:relative; box-shadow: 0 14px 44px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08); }
.rp-head::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 30%, var(--hair) 70%, transparent 95%); }
.rp-head::after { content:''; position:absolute; left:0; top:14px; bottom:14px; width:5px;
  background: linear-gradient(180deg, transparent, var(--amber) 25%, var(--amber-2) 75%, transparent);
  box-shadow: 0 0 20px rgba(255,176,42,.78), 0 0 40px rgba(255,176,42,.3); }
.rp-head-eyebrow { display:block; color:var(--amber); font-family:var(--fm); font-size:9.5px; letter-spacing:.34em;
  text-shadow: 0 0 12px rgba(255,176,42,.7), 0 0 24px rgba(255,176,42,.32); margin-bottom:8px; }
.rp-title { margin:.05em 0 .35em; font-size:clamp(34px,4vw,56px); line-height:1; }
.rp-copy { margin:6px 0 0; max-width:64ch; color:var(--ink-2); font-size:12.5px; letter-spacing:.04em;
  text-transform:none; line-height:1.6; }
.rp-headclose { margin-top:6px; }

.rp-grid { flex:1 1 auto; display:grid; gap:clamp(12px,1.4vw,20px); align-content:start;
  grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); overflow:auto; padding:8px 2px 4px; }
.rp-card { position:relative; display:flex; gap:16px; padding:20px;
  background:
    linear-gradient(180deg, rgba(22,30,40,.94) 0%, rgba(6,10,14,.96) 100%);
  border:1px solid rgba(190,214,230,.26); clip-path:var(--shear);
  transition:border-color .2s ease, transform .2s ease, box-shadow .2s ease;
  box-shadow: 0 10px 28px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.06);
  overflow:hidden; }
.rp-card::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 50%, transparent 95%); }
.rp-card::after { content:''; position:absolute; right:0; top:0; bottom:0; width:1px;
  background: linear-gradient(180deg, transparent, rgba(190,214,230,.16), transparent); }
.rp-card:hover { transform: translateY(-4px); border-color: rgba(255,176,42,.7);
  box-shadow: 0 18px 44px rgba(0,0,0,.65), 0 0 32px rgba(255,176,42,.18), inset 0 1px 0 rgba(255,255,255,.1); }
.rp-card.rp-active { border-color:rgba(255,176,42,.85);
  box-shadow: 0 10px 28px rgba(0,0,0,.55), 0 0 36px rgba(255,176,42,.34), inset 0 0 0 1px rgba(255,176,42,.32); }
.rp-card.rp-active::before { background: linear-gradient(90deg, transparent, var(--amber), transparent); }
.rp-card.rp-active::after { background: linear-gradient(180deg, transparent, var(--amber), transparent); }
.rp-card.rp-locked { opacity:.85; }
.rp-card.rp-locked::after { content:''; position:absolute; inset:0; pointer-events:none; z-index:4;
  background: repeating-linear-gradient(45deg, transparent 0 7px, rgba(0,0,0,.55) 7px 8px); }

.rp-portrait { position:relative; flex:0 0 auto; width:114px; aspect-ratio:3/4; overflow:hidden;
  border:1px solid rgba(255,176,42,.4);
  background:
    linear-gradient(180deg, transparent 0%, transparent 60%, rgba(0,0,0,.55) 100%),
    linear-gradient(160deg, rgba(24,32,40,.95), rgba(6,9,13,.98));
  box-shadow:
    inset 0 0 0 1px rgba(255,176,42,.12),
    inset 0 0 36px rgba(0,0,0,.6),
    0 0 18px rgba(255,176,42,.18); }
.rp-portrait::before { content:''; position:absolute; left:0; right:0; top:0; height:18px;
  background: linear-gradient(180deg, rgba(255,176,42,.2), transparent);
  z-index:2; }
.rp-portrait::after { content:''; position:absolute; inset:0; pointer-events:none; z-index:2;
  background:
    linear-gradient(180deg, transparent 0%, transparent 55%, rgba(0,0,0,.65) 100%); }
.rp-portrait svg { position:absolute; inset:0; width:100%; height:100%; display:block; }
.rp-card.rp-locked .rp-portrait svg { filter:grayscale(.8) brightness(.45); }
.rp-lockover { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center;
  justify-content:center; gap:6px; background:rgba(4,6,9,.7); text-align:center; padding:6px; z-index:3; }
.rp-lockicon { font-size:22px; color:var(--red); text-shadow: 0 0 16px rgba(255,63,49,.78), 0 0 28px rgba(255,63,49,.32); }
.rp-locktext { font-family:var(--fm); font-size:8.5px; letter-spacing:.18em; color:#fff; font-weight:700;
  text-shadow: 0 0 6px rgba(0,0,0,.9), 0 1px 0 rgba(0,0,0,.5); }

.rp-body { flex:1 1 auto; min-width:0; display:flex; flex-direction:column; gap:8px; }
.rp-top { display:flex; align-items:baseline; justify-content:space-between; gap:8px; }
.rp-name { font-family:var(--fd); font-size:22px; font-weight:800; letter-spacing:.06em; color:#fff;
  text-shadow: 0 2px 10px rgba(0,0,0,.7), 0 0 20px rgba(255,255,255,.12); }
.rp-kit { font-family:var(--fm); font-size:9px; font-weight:700; letter-spacing:.2em; color:var(--amber-3);
  padding:3px 9px; border:1px solid rgba(255,176,42,.55); white-space:nowrap;
  background: linear-gradient(180deg, rgba(255,176,42,.18), rgba(255,176,42,.05));
  text-shadow: 0 0 10px rgba(255,176,42,.5); box-shadow: inset 0 1px 0 rgba(255,224,156,.18); }
.rp-tagline { margin:0; font-size:11.5px; letter-spacing:.03em; text-transform:none; color:var(--ink-2);
  line-height:1.55; padding:8px 0; border-top:1px solid rgba(190,214,230,.14); border-bottom:1px solid rgba(190,214,230,.14);
  font-style:italic; }

.rp-stats { display:flex; flex-direction:column; gap:4px; }
.rp-stat-row { display:flex; align-items:center; justify-content:space-between; gap:10px;
  font-size:10.5px; letter-spacing:.14em; padding-bottom:6px; border-bottom:1px solid rgba(190,214,230,.12); }
.rp-stat-row:last-child { border-bottom:0; }
.rp-stat-k { color:var(--ink-3); }
.rp-stat-v { color:#fff; font-family:var(--fm); text-align:right; text-shadow: 0 0 8px rgba(0,0,0,.4); font-weight:600; }
.rp-stat-v.rp-stat-high { color:var(--ok); text-shadow: 0 0 10px rgba(168,232,106,.55); }

.rp-record { display:grid; grid-template-columns:repeat(4,1fr); gap:4px; margin-top:4px; padding:12px 4px;
  background:
    linear-gradient(180deg, rgba(4,7,11,.6), rgba(8,13,17,.65)),
    radial-gradient(80% 100% at 50% 0%, rgba(255,176,42,.08), transparent 70%);
  border-top:1px solid rgba(190,214,230,.14); border-bottom:1px solid rgba(190,214,230,.14);
  position:relative; }
.rp-record::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(255,176,42,.4), transparent); }
.rp-record::after { content:''; position:absolute; left:0; right:0; bottom:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(255,176,42,.2), transparent); }
.rp-record-cell { display:flex; flex-direction:column; gap:2px; align-items:center; padding:4px 0;
  position:relative; }
.rp-record-cell + .rp-record-cell::before { content:''; position:absolute; left:0; top:18%; bottom:18%; width:1px;
  background: linear-gradient(180deg, transparent, rgba(190,214,230,.18), transparent); }
.rp-record-v { font-family:var(--fd); font-size:16px; font-weight:800; color:var(--amber-3);
  text-shadow: 0 0 12px rgba(255,176,42,.55), 0 0 22px rgba(255,176,42,.22); }
.rp-record-k { font-size:7.5px; letter-spacing:.2em; color:var(--ink-3); }

.rp-action { margin-top:auto; align-self:flex-start; font-weight:800; padding:11px 24px; }
.rp-card.rp-active .rp-action { border-color:rgba(255,176,42,.85); color:#fff;
  background:linear-gradient(180deg, rgba(255,176,42,.3), rgba(255,176,42,.1));
  box-shadow: 0 0 24px rgba(255,176,42,.4), inset 0 1px 0 rgba(255,224,156,.3); }

.rp-foot { display:flex; align-items:center; gap:16px; flex:0 0 auto;
  padding:14px 22px; background:linear-gradient(90deg, rgba(12,18,24,.92), rgba(8,13,17,.7));
  border:1px solid rgba(190,214,230,.26);
  clip-path: var(--shear-sm);
  position:relative; }
.rp-foot::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(190,214,230,.4), transparent); }
.rp-flash { font-family:var(--fm); font-size:10px; letter-spacing:.16em; color:var(--amber-3); opacity:0; transition:opacity .2s ease;
  text-shadow: 0 0 12px rgba(255,176,42,.6); display:flex; align-items:center; gap:8px; }
.rp-flash::before { content:''; width:18px; height:1px; background:var(--amber); box-shadow: 0 0 8px var(--amber); }
.rp-flash-on { opacity:1; }
.rp-close { margin-left:auto; padding:12px 30px; font-weight:800; }
`;
